import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import formatError from "../../services/FormatError";
import { fetchBuyerDetails } from "../../services/BuyerService";
import { getStatusBadge } from "./Business";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";

function BuyerDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const [buyerDetails, setBuyerDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPartner, setShowPartner] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  useEffect(() => {
    getBuyerDetails();
  }, []);

  const getBuyerDetails = () => {
    setLoading(true);
    fetchBuyerDetails(params?.buyerId)
      .then((res) => {
        setBuyerDetails(res?.data?.result);
      })
      .catch((error) => {
        formatError(error.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onBack = () => {
    navigate("/buyer");
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "") return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const InfoCard = ({ icon, label, value, variant = "primary" }) => (
    <div className="col-lg-4 col-md-6 mb-4">
      <div className={`info-card ${variant}`}>
        <div className="info-card-icon">{icon}</div>
        <div className="info-card-content">
          <span className="info-card-label">{label}</span>
          <p className="info-card-value">{value}</p>
        </div>
      </div>
    </div>
  );

  const ContactItem = ({ label, value }) => (
    <div className="contact-item">
      <span className="contact-label">{label}:</span>
      <span className="contact-value">{value || "N/A"}</span>
    </div>
  );

  const TagList = ({ title, items, icon }) => (
    <div className="tag-section">
      <h6 className="tag-section-title">
        <i className={icon}></i>
        {title}
      </h6>
      <div className="tag-container">
        {items?.length > 0 ? (
          items.map((item, index) => (
            <span key={index} className="custom-tag">
              {item}
            </span>
          ))
        ) : (
          <span className="no-data-text">{t("hnoavailable")}</span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="col-lg-12">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("hloading")}</span>
          </div>
          <p className="mt-3 text-muted">{t("hloadingbuyer")}</p>
        </div>
      </div>
    );
  }

  if (Object.keys(buyerDetails)?.length === 0) {
    return (
      <div className="col-lg-12">
        <div className="empty-state">
          <i className="fa fa-user-times fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">{t("hnobuyerdetails")}</h5>
          <button className="btn btn-primary mt-3" onClick={onBack}>
            <i className="fa fa-arrow-left me-2"></i>
            {t("hbackbuyers")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      {/* Profile Header */}
      <div className="profile-header">
        <button className="back-button" onClick={onBack}>
          <i className="fa fa-arrow-left"></i>
          {t("hbackbuyer")}
        </button>

        <h1 className="profile-title">{buyerDetails?.name}</h1>

        <div className="profile-subtitle mb-0">
          {buyerDetails?.domain} • {buyerDetails?.occupation}
        </div>

        <div className="profile-meta">
          {buyerDetails?.buyer_type && (
            <div
              onClick={() => {
                buyerDetails?.buyer_type === "partner_buyer" &&
                  setShowPartner(true);
              }}
              className="website-link cursor-pointer"
            >
              <i className="fa fa-users me-2" aria-hidden="true"></i>
              {buyerDetails?.buyer_type === "partner_buyer"
                ? "Partner Buyer"
                : "Single Buyer"}
            </div>
          )}
          {buyerDetails?.website && buyerDetails?.website !== "" && (
            <a
              href={buyerDetails.website}
              target="_blank"
              rel="noopener noreferrer"
              className="website-link"
            >
              <i className="fa fa-globe me-2"></i>
              {t("hvisitwebsite")}
            </a>
          )}

          <div
            onClick={() => setShowAgreement(true)}
            className="website-link cursor-pointer"
          >
            <i className="fa fa-file-text-o me-2" aria-hidden="true"></i>
            {t("agreement")}
          </div>

          {buyerDetails?.currency?.code && (
            <span className="currency-highlight">
              <i className="fa fa-coins me-2"></i>
              {buyerDetails.currency.code}
            </span>
          )}

          <span
            className={`status-badge ${getStatusBadge(buyerDetails?.status)}`}
          >
            {t(buyerDetails?.status)}
          </span>
        </div>
      </div>

      {/* Important Dates Section */}
      <div className="info-cards-section">
        <h3 className="section-title">
          <i className="fa fa-calendar-alt"></i>
          {t("himportantDate")}
        </h3>
        <div className="row">
          <InfoCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M19 5H18V3C18 2.73478 17.8946 2.48043 17.7071 2.29289C17.5196 2.10536 17.2652 2 17 2C16.7348 2 16.4804 2.10536 16.2929 2.29289C16.1054 2.48043 16 2.73478 16 3V5H8V3C8 2.73478 7.89464 2.48043 7.70711 2.29289C7.51957 2.10536 7.26522 2 7 2C6.73478 2 6.48043 2.10536 6.29289 2.29289C6.10536 2.48043 6 2.73478 6 3V5H5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V9H22V8C22 7.20435 21.6839 6.44129 21.1213 5.87868C20.5587 5.31607 19.7957 5 19 5Z" />
                <path d="M2 19C2 19.7956 2.31607 20.5587 2.87868 21.1213C3.44129 21.6839 4.20435 22 5 22H19C19.7957 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V11H2V19Z" />
              </svg>
            }
            label={t("buyerDetailsCreatedDate")}
            value={formatDate(buyerDetails?.createdat)}
            variant="primary"
          />

          <InfoCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M19 5H18V3C18 2.73478 17.8946 2.48043 17.7071 2.29289C17.5196 2.10536 17.2652 2 17 2C16.7348 2 16.4804 2.10536 16.2929 2.29289C16.1054 2.48043 16 2.73478 16 3V5H8V3C8 2.73478 7.89464 2.48043 7.70711 2.29289C7.51957 2.10536 7.26522 2 7 2C6.73478 2 6.48043 2.10536 6.29289 2.29289C6.10536 2.48043 6 2.73478 6 3V5H5C4.20435 5 3.44129 5.31607 2.87868 5.87868C2.31607 6.44129 2 7.20435 2 8V9H22V8C22 7.20435 21.6839 6.44129 21.1213 5.87868C20.5587 5.31607 19.7957 5 19 5Z" />
                <path d="M2 19C2 19.7956 2.31607 20.5587 2.87868 21.1213C3.44129 21.6839 4.20435 22 5 22H19C19.7957 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V11H2V19Z" />
              </svg>
            }
            label={t("buyerDetailsPostedDate")}
            value={formatDate(buyerDetails?.posteddate)}
            variant="success"
          />

          <InfoCard
            icon={
              <svg
                viewBox="0 0 19 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M18.6601 8.6858C18.5437 8.44064 18.2965 8.28427 18.025 8.28427H10.9728L15.2413 1.06083C15.3697 0.843469 15.3718 0.573844 15.2466 0.354609C15.1214 0.135375 14.8884 -9.37014e-05 14.6359 4.86277e-08L8.61084 0.000656299C8.3608 0.000750049 8.12957 0.1335 8.00362 0.349453L0.0958048 13.905C-0.0310859 14.1224 -0.0319764 14.3911 0.0934142 14.6094C0.218805 14.8277 0.451352 14.9624 0.703117 14.9624H7.71037L5.66943 23.1263C5.58955 23.4457 5.74213 23.7779 6.03651 23.9255C6.13691 23.9757 6.24459 24 6.35123 24C6.55729 24 6.75923 23.9094 6.89638 23.7413L18.5699 9.43186C18.7415 9.22148 18.7766 8.93105 18.6601 8.6858V8.6858Z" />
              </svg>
            }
            label={t("buyerDetailsServiceStartDate")}
            value={formatDate(buyerDetails?.startservice)}
            variant="danger"
          />
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="contact-section">
        <h3 className="section-title">
          <i className="fa fa-address-book"></i>
          {t("hcontactInfos")}
        </h3>

        <div className="contact-grid">
          {/* Contact Details */}
          <div className="contact-group">
            <h6>
              <i className="fa fa-user"></i>
              {t("hpersonaldetails")}
            </h6>
            <ContactItem
              label={t("nameInput")}
              value={buyerDetails?.contactname}
            />
            <ContactItem
              label={t("emailInput")}
              value={buyerDetails?.contactemail}
            />
            <ContactItem
              label={t("secondaryEmailInput")}
              value={buyerDetails?.email}
            />
            <ContactItem label={t("phoneInput")} value={buyerDetails?.phone} />
            <ContactItem
              label={t("secondaryPhoneInput")}
              value={buyerDetails?.phone2}
            />
            <ContactItem label={t("faxInput")} value={buyerDetails?.fax} />
            <ContactItem
              label={t("currencyInput")}
              value={buyerDetails?.currency?.code}
            />
          </div>

          {/* Address Details */}
          <div className="contact-group">
            <h6>
              <i className="fa fa-map-marker-alt"></i>
              {t("haddressInvestment")}
            </h6>
            <ContactItem
              label={t("addressInput")}
              value={buyerDetails?.address}
            />
            <ContactItem label={t("cityInput")} value={buyerDetails?.city} />
            <ContactItem
              label={t("countryInput")}
              value={buyerDetails?.country}
            />
            <ContactItem
              label={t("regionInput")}
              value={buyerDetails?.region}
            />
            <ContactItem
              label={t("postalCodeInput")}
              value={buyerDetails?.postalcode}
            />
            <ContactItem
              label={t("investmentRangeInput")}
              value={buyerDetails?.investmentrange}
            />
          </div>
        </div>
      </div>

      {/* Interests Section */}
      <div className="row mt-4">
        <div className="col-lg-6">
          <TagList
            title={t("buyerDetailsInterestedIndustries")}
            items={buyerDetails?.industries}
            icon="fa fa-industry"
          />
        </div>

        <div className="col-lg-6">
          <TagList
            title={t("buyerDetailsInterestedLocations")}
            items={buyerDetails?.interestedlocations}
            icon="fa fa-map-marker-alt"
          />
        </div>
      </div>

      <Modal
        show={showAgreement}
        onHide={() => setShowAgreement(false)}
        backdrop={true} // Allows clicking outside to close
        keyboard={true} // Allows ESC key to close
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("agreement_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="my-3">
            <ol style={{ paddingLeft: "1.5rem" }}>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_introduction")}
              </li>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_prohibited_activities")}
              </li>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_dispute_resolution")}
              </li>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_limitation_liabilities")}
              </li>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_returns_refund")}
              </li>
              <li className="my-2" style={{ fontSize: "15px" }}>
                {t("agreement_contact_information")}
              </li>
            </ol>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div>
            {buyerDetails?.createdat
              ? `${t("agreement_signed_on")} ${new Date(
                  buyerDetails.createdat
                ).toDateString()}`
              : ""}
          </div>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showPartner}
        onHide={() => setShowPartner(false)}
        backdrop={true}
        keyboard={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("partnersInformation")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th scope="col" className="text-center">
                    <i className="bi bi-person me-2"></i>
                    {t("nameInput")}
                  </th>
                  <th scope="col" className="text-center">
                    <i className="bi bi-envelope me-2"></i>
                    {t("emailInput")}
                  </th>
                  <th scope="col" className="text-center">
                    <i className="bi bi-percent me-2"></i>
                    {t("percentage")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {buyerDetails?.partners?.map((partner, idx) => (
                  <tr key={idx}>
                    <td className="fw-medium">{partner.partner_name}</td>
                    <td className="text-muted">{partner.partner_email}</td>
                    <td className="text-center">
                      <span className="badge bg-primary rounded-pill">
                        {partner.partnership_percentage}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default BuyerDetails;
